import { c as _c } from "react/compiler-runtime";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { BuyerAssistantMessage, BuyerAssistantProduct, BuyerAssistantShop } from "@/api/use-buyer-assistant";
import { productHref } from "@/lib/product-link";
import { cn } from "@/lib/utils";
import { formatRub, keepNumbersTogether } from "./format";

// Сообщение ленты помощника. Пузыри — как в «Чатах» (r16, pad 10, 14/18):
// покупатель справа на тёмном, помощник слева на белом. Текст помощника —
// обычный текст с переносами строк, без markdown. Под ответом — найденные
// товары (горизонтальная лента карточек) и магазины (плашки), ниже серая
// пометка, если помощник смотрел тренды в открытых источниках.
export function AssistantMessage(t0) {
  const $ = _c(24);
  const {
    message,
    onNavigate
  } = t0;
  const {
    t
  } = useTranslation();
  const mine = message.role === "user";
  const t1 = message.role;
  const t2 = mine ? "items-end" : "items-start";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex min-w-0 flex-col gap-2", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== message.content || $[3] !== mine) {
    t4 = message.content && <p className={cn("max-w-[85%] rounded-[16px] px-3 py-2.5 text-[14px] leading-[19px] whitespace-pre-line break-words", mine ? "rounded-br-[6px] bg-ds-primary text-white" : "rounded-bl-[6px] bg-white text-ds-primary")}>{mine ? message.content : keepNumbersTogether(message.content)}</p>;
    $[2] = message.content;
    $[3] = mine;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== message.products || $[6] !== mine || $[7] !== onNavigate) {
    t5 = !mine && message.products.length > 0 && <ProductRail products={message.products} onNavigate={onNavigate} />;
    $[5] = message.products;
    $[6] = mine;
    $[7] = onNavigate;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== message.shops || $[10] !== mine || $[11] !== onNavigate) {
    t6 = !mine && message.shops.length > 0 && <ShopList shops={message.shops} onNavigate={onNavigate} />;
    $[9] = message.shops;
    $[10] = mine;
    $[11] = onNavigate;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== message.usedWeb || $[14] !== mine || $[15] !== t) {
    t7 = !mine && message.usedWeb && <p className="flex items-center gap-1 px-1 text-[11px] leading-[14px] text-black/40"><Globe className="size-3" strokeWidth={1.75} aria-hidden="true" />{t("assistant.usedWeb")}</p>;
    $[13] = message.usedWeb;
    $[14] = mine;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== message.role || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div data-assistant-role={t1} className={t3}>{t4}{t5}{t6}{t7}</div>;
    $[17] = message.role;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}

// Лента карточек 132px: фото 3:4 r12, цена (+ зачёркнутая старая), бренд,
// название в две строки, магазин. Скроллится внутри ленты, страница не едет;
// scroll-padding держит первую карточку на отступе 16, а не у края панели.
function ProductRail(t0) {
  const $ = _c(12);
  const {
    products,
    onNavigate
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("assistant.products");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onNavigate || $[3] !== products || $[4] !== t) {
    let t3;
    if ($[6] !== onNavigate || $[7] !== t) {
      t3 = p => <li key={p.id} className="w-[132px] shrink-0 snap-start"><Link to={productHref(p.id)} onClick={onNavigate} className="group flex flex-col gap-1.5 rounded-[14px] bg-white p-1.5 outline-none focus-visible:ring-2 focus-visible:ring-ds-focus" data-testid="assistant-product"><span className="block aspect-[3/4] w-full overflow-hidden rounded-[10px] bg-[#F2F2F2]">{p.photo && <img src={p.photo} alt="" loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />}</span><span className="flex flex-col gap-0.5 px-0.5 pb-0.5"><span className="flex flex-wrap items-baseline gap-x-1.5"><span className="text-[14px] leading-[18px] font-bold whitespace-nowrap text-ds-primary">{formatRub(t, p.price)}</span>{p.oldPrice != null && p.oldPrice > p.price && <span className="text-[11px] leading-[14px] whitespace-nowrap text-ds-muted line-through">{formatRub(t, p.oldPrice)}</span>}</span>{p.brand && <span className="truncate text-[12px] leading-[16px] font-semibold text-osmix-text-1">{p.brand}</span>}<span className="line-clamp-2 text-[12px] leading-[15px] text-ds-muted">{p.name}</span>{p.shopName && <span className="truncate text-[11px] leading-[14px] text-black/40">{p.shopName}</span>}</span></Link></li>;
      $[6] = onNavigate;
      $[7] = t;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = products.map(t3);
    $[2] = onNavigate;
    $[3] = products;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <ul className="-mx-4 flex w-[calc(100%+2rem)] snap-x scroll-px-4 gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label={t1}>{t2}</ul>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

// Плашки магазинов: логотип 32, имя, сколько товаров.
function ShopList(t0) {
  const $ = _c(12);
  const {
    shops,
    onNavigate
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("assistant.shops");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onNavigate || $[3] !== shops || $[4] !== t) {
    let t3;
    if ($[6] !== onNavigate || $[7] !== t) {
      t3 = s => <li key={s.slug}><Link to={`/shop/${encodeURIComponent(s.slug)}`} onClick={onNavigate} className="flex items-center gap-2.5 rounded-[14px] bg-white p-2 outline-none transition-colors hover:bg-white/70 focus-visible:ring-2 focus-visible:ring-ds-focus" data-testid="assistant-shop"><span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[#F2F2F2] text-[13px] font-semibold text-ds-primary">{s.logoUrl ? <img src={s.logoUrl} alt="" className="size-full object-cover" /> : s.name.slice(0, 1)}</span><span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-[14px] leading-[18px] font-semibold text-ds-primary">{s.name}</span>{s.productCount != null && s.productCount > 0 && <span className="text-[12px] leading-4 text-black/50">{t("assistant.shopProducts", {
                count: s.productCount
              })}</span>}</span></Link></li>;
      $[6] = onNavigate;
      $[7] = t;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = shops.map(t3);
    $[2] = onNavigate;
    $[3] = shops;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <ul className="flex w-full flex-col gap-1.5" aria-label={t1}>{t2}</ul>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}